// src/services/mappers/dashboardMapper.ts

import {
  DashboardData,
  DashboardChartData,
  ProjectionsClientBalanceDetailResponse,
  DashboardAssetBreakdown,
} from '@typeDefs/domain/dashboard';

/**
 * Dashboard data mapper - transforms API responses to domain objects
 * Following the same pattern as treasuryBillMapper
 */
export const dashboardMapper = {
  /**
   * Transform projections API response to dashboard data
   */
  fromProjectionsResponse: (response: ProjectionsClientBalanceDetailResponse): DashboardData => {
    if (import.meta.env.MODE !== 'test') {
      console.log('=== DASHBOARD MAPPER - RAW API RESPONSE ===');
      console.log('Full response:', response);
      console.log('Response keys:', Object.keys(response || {}));
      console.log('==========================================');
    }

    // Map the correct field names from the API response.
    // Gross binding (PreservoirReserves/preservoir-ai#117): the displayed balance is the GROSS
    // cash ledger figure — daily fee accrual must not erode it; only the monthly extraction
    // (a real ledger movement) moves it. The net figure is retained under the unambiguous name
    // netAvailableCash (never `availableBalance` — that's the virtualCashAccount concept).
    const accountBalance = response.cashLedgerBalance || 0;
    const netAvailableCash = response.availableCash || 0;
    const totalAssets = response.totalAssetValue || 0;
    const pendingPayments = response.reservedCash || 0;
    const pendingIncoming = response.pendingIncomingSettlement || 0;
    const lifetimeEarnings = response.lifetimeEarnings || 0;
    const totalPortfolioValue = response.totalPortfolioValue || 0;

    if (import.meta.env.MODE !== 'test') {
      console.log('=== DASHBOARD MAPPER - MAPPED VALUES ===');
      console.log('Account Balance (gross):', accountBalance);
      console.log('Net Available Cash:', netAvailableCash);
      console.log('Total Assets:', totalAssets);
      console.log('Pending Payments:', pendingPayments);
      console.log('Lifetime Earnings:', lifetimeEarnings);
      console.log('Total Portfolio Value:', totalPortfolioValue);
      console.log('========================================');
    }

    const mappedData = {
      accountBalance,
      netAvailableCash,
      lifetimeEarnings,
      totalAssets,
      pendingPayments,
      pendingIncoming,
      totalPortfolioValue,
      lastUpdated: new Date(),
      assetsBreakdown: [], // Will be populated when we have asset breakdown data
    };

    if (import.meta.env.MODE !== 'test') {
      console.log('=== DASHBOARD MAPPER - FINAL DATA ===');
      console.log('Mapped Data:', mappedData);
      console.log('Total Value:', accountBalance + totalAssets + pendingPayments);
      console.log('=====================================');
    }

    return mappedData;
  },

  /**
   * Calculate total portfolio value — computed locally from GROSS components.
   * The API totalPortfolioValue is net-based (shared-core ClientDailyBalance), so a present
   * server figure is deliberately IGNORED: the displayed total must stay consistent with the
   * gross accountBalance segment (PreservoirReserves/preservoir-ai#117).
   */
  calculateTotalValue: (data: DashboardData): number => {
    return data.accountBalance + data.totalAssets + data.pendingPayments + data.pendingIncoming;
  },

  /**
   * Calculate chart data with percentages for visualization
   */
  calculateChartData: (data: DashboardData): DashboardChartData => {
    const total = dashboardMapper.calculateTotalValue(data);

    // Avoid division by zero
    if (total === 0) {
      return {
        accountBalance: { value: 0, percentage: 0, color: '--chart-account-balance' },
        assets: { value: 0, percentage: 0, color: '--chart-assets' },
        pendingPayments: { value: 0, percentage: 0, color: '--chart-pending-payments' },
        incomingPayments: { value: 0, percentage: 0, color: '--chart-incoming-payments' },
        total: 0,
      };
    }

    return {
      accountBalance: {
        value: data.accountBalance,
        percentage: (data.accountBalance / total) * 100,
        color: '--chart-account-balance',
      },
      assets: {
        value: data.totalAssets,
        percentage: (data.totalAssets / total) * 100,
        color: '--chart-assets',
      },
      pendingPayments: {
        value: data.pendingPayments,
        percentage: (data.pendingPayments / total) * 100,
        color: '--chart-pending-payments',
      },
      incomingPayments: {
        value: data.pendingIncoming,
        percentage: (data.pendingIncoming / total) * 100,
        color: '--chart-incoming-payments',
      },
      total,
    };
  },

  /**
   * Calculate chart segments for SVG circle rendering
   */
  calculateChartSegments: (data: DashboardData, radius: number = 70) => {
    const chartData = dashboardMapper.calculateChartData(data);
    const circumference = 2 * Math.PI * radius;
    const gapSize = 15; // Current gap size from Dashboard component

    // Calculate segment lengths with gaps
    const accountLength = Math.max(
      0,
      (chartData.accountBalance.percentage / 100) * circumference - gapSize,
    );
    const assetsLength = Math.max(0, (chartData.assets.percentage / 100) * circumference - gapSize);
    const pendingLength = Math.max(
      0,
      (chartData.pendingPayments.percentage / 100) * circumference - gapSize,
    );
    const incomingLength = Math.max(
      0,
      (chartData.incomingPayments.percentage / 100) * circumference - gapSize,
    );

    // Calculate offsets accounting for gaps.
    // Keep-gap policy: every segment reserves its 15px gap regardless of value, so the
    // cumulative offset always advances by (previous length + gapSize) per preceding segment.
    const accountOffset = 0;
    const assetsOffset = -(accountLength + gapSize);
    const pendingOffset = -(accountLength + gapSize + assetsLength + gapSize);
    const incomingOffset = -(
      accountLength +
      gapSize +
      assetsLength +
      gapSize +
      pendingLength +
      gapSize
    );

    return {
      circumference,
      segments: {
        accountBalance: {
          length: accountLength,
          offset: accountOffset,
          color: chartData.accountBalance.color,
          percentage: chartData.accountBalance.percentage,
          value: chartData.accountBalance.value,
        },
        assets: {
          length: assetsLength,
          offset: assetsOffset,
          color: chartData.assets.color,
          percentage: chartData.assets.percentage,
          value: chartData.assets.value,
        },
        pendingPayments: {
          length: pendingLength,
          offset: pendingOffset,
          color: chartData.pendingPayments.color,
          percentage: chartData.pendingPayments.percentage,
          value: chartData.pendingPayments.value,
        },
        incomingPayments: {
          length: incomingLength,
          offset: incomingOffset,
          color: chartData.incomingPayments.color,
          percentage: chartData.incomingPayments.percentage,
          value: chartData.incomingPayments.value,
        },
      },
      total: chartData.total,
    };
  },

  /**
   * Create default asset breakdown when no data is available
   */
  createDefaultAssetBreakdown: (): DashboardAssetBreakdown[] => {
    return [
      {
        type: 'treasury_bills',
        value: 0,
        count: 0,
        averageYield: 0,
      },
      {
        type: 'bonds',
        value: 0,
        count: 0,
        averageYield: 0,
      },
      {
        type: 'other',
        value: 0,
        count: 0,
        averageYield: 0,
      },
    ];
  },

  /**
   * Apply update to existing dashboard data
   */
  applyUpdate: (
    currentData: DashboardData,
    update: { type: string; amount: number },
  ): DashboardData => {
    const updatedData = { ...currentData };

    switch (update.type) {
      case 'balance_update':
        updatedData.accountBalance = Math.max(0, currentData.accountBalance + update.amount);
        break;

      case 'asset_purchase':
        updatedData.accountBalance = Math.max(0, currentData.accountBalance - update.amount);
        updatedData.totalAssets = currentData.totalAssets + update.amount;
        break;

      case 'asset_sale':
        updatedData.accountBalance = currentData.accountBalance + update.amount;
        updatedData.totalAssets = Math.max(0, currentData.totalAssets - update.amount);
        break;

      case 'payment_received':
        updatedData.pendingPayments = Math.max(0, currentData.pendingPayments - update.amount);
        updatedData.accountBalance = currentData.accountBalance + update.amount;
        break;

      case 'payment_sent':
        updatedData.accountBalance = Math.max(0, currentData.accountBalance - update.amount);
        updatedData.pendingPayments = currentData.pendingPayments + update.amount;
        break;

      default:
        console.warn(`Unknown dashboard update type: ${update.type}`);
        return currentData;
    }

    updatedData.lastUpdated = new Date();
    return updatedData;
  },

  /**
   * Check if data is stale (older than specified minutes)
   */
  isDataStale: (data: DashboardData | null, staleAfterMinutes: number = 5): boolean => {
    if (!data || !data.lastUpdated) return true;

    const staleThreshold = staleAfterMinutes * 60 * 1000; // Convert to milliseconds
    return Date.now() - data.lastUpdated.getTime() > staleThreshold;
  },
};
